/**
 * Centralized allowlist of "Meet-specific" telemetry domains.
 *
 * In Module Federation (TeamHub-hosted) mode, NG Meet shares the page with the
 * host, so its globally-installed OpenTelemetry instrumentation would otherwise
 * capture the host's traffic too. These helpers let the telemetry layer collect
 * spans/metrics only for requests that belong to Meet (SM-1107).
 *
 * Allowed:
 *   - *.meetings.sangoma.com (frontend + api.*; e.g. observer.* is excluded
 *     separately to avoid self-tracing)
 *   - *.ngmeet.sangoma.com (alternate frontend hostnames)
 *   - 8x8.vc and subdomains (Jitsi/JaaS)
 *   - api.ipify.org (client-IP lookup; see clientMetadata)
 */

/** Apex domains whose hostname (and any subdomain) counts as Meet traffic. */
const MEET_APEX_DOMAINS = ['meetings.sangoma.com', 'ngmeet.sangoma.com', '8x8.vc'] as const;

/** Standalone hostnames (no subdomain wildcard) that count as Meet traffic. */
const MEET_EXACT_HOSTNAMES = ['api.ipify.org'] as const;

/**
 * Source-of-truth regex used to build both the positive predicate and the
 * negative-lookahead ignore pattern. Matches a hostname that is exactly an apex
 * domain or any subdomain of one.
 */
const apexAlternation = MEET_APEX_DOMAINS.map((d) => d.replace(/\./g, '\\.')).join('|');
const exactAlternation = MEET_EXACT_HOSTNAMES.map((d) => d.replace(/\./g, '\\.')).join('|');

const MEET_HOSTNAME_REGEX = new RegExp(
  `^(?:(?:[a-z0-9-]+\\.)*(?:${apexAlternation})|${exactAlternation})$`,
  'i',
);

/**
 * True when `url` targets a Meet-specific domain and should be instrumented even
 * in hosted mode. Returns `false` for unparseable input (fail closed).
 */
export function isMeetTelemetryUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url, window.location.href);
    return MEET_HOSTNAME_REGEX.test(hostname);
  } catch {
    return false;
  }
}

/**
 * Negative-lookahead pattern that MATCHES any URL whose host is NOT on the Meet
 * allowlist. Intended for OpenTelemetry instrumentation `ignoreUrls` lists in
 * hosted mode: anything it matches is skipped, so only Meet-domain requests get
 * spans/metrics.
 *
 * NOTE: This intentionally still matches Meet sub-services that must be excluded
 * for other reasons (e.g. `observer.*` self-tracing). Callers MUST keep those
 * specific exclusions ahead of this pattern in the `ignoreUrls` array.
 */
export const NON_MEET_URL_IGNORE_PATTERN = new RegExp(
  `^(?!https?://(?:(?:[a-z0-9-]+\\.)*(?:${apexAlternation})|${exactAlternation})(?:[:/?#]|$))`,
  'i',
);
